import React from 'react';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import type { ExternalApp } from '../../services/externalApps';
import { i18nService } from '../../services/i18n';
import { useLanguage } from '../../hooks/useLanguage';

interface ExternalAppViewProps {
  app: ExternalApp;
  onNavigateHome: () => void;
}

/**
 * Shows a sibling app inside an iframe so this app's chrome (sidebar, theme)
 * stays visible.
 *
 * Caveats:
 * - The target must not send `X-Frame-Options` / CSP `frame-ancestors`; if it
 *   starts doing so the frame stays blank — the "open in a new tab" button is
 *   the escape hatch.
 * - Cross-origin means separate cookies/localStorage, so the embedded app may
 *   ask for its own login. Do not pass tokens via the URL.
 */
const ExternalAppView: React.FC<ExternalAppViewProps> = ({ app, onNavigateHome }) => {
  // The header and the frame's title are translated; subscribe so switching the
  // language updates them without a reload.
  useLanguage();
  const label = app.labelKey ? i18nService.t(app.labelKey) : app.label;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="draggable flex h-12 shrink-0 items-center justify-between gap-3 border-b dark:border-claude-darkBorder border-claude-border px-4">
        <div className="non-draggable flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={onNavigateHome}
            className="h-8 px-3 inline-flex items-center gap-1.5 rounded-lg dark:text-claude-darkTextSecondary text-claude-textSecondary hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover transition-colors text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            {i18nService.t('back')}
          </button>
          <span className="truncate text-lg font-semibold dark:text-claude-darkText text-claude-text">
            {label}
          </span>
        </div>
        <button
          type="button"
          onClick={() => window.open(app.url, '_blank', 'noopener')}
          title={i18nService.t('openInNewTab')}
          aria-label={i18nService.t('openInNewTab')}
          className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-lg px-2 text-xs font-medium dark:text-claude-darkTextSecondary text-claude-textSecondary hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover transition-colors"
        >
          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
          {i18nService.t('openInNewTab')}
        </button>
      </div>
      <iframe
        src={app.url}
        title={label}
        className="w-full flex-1 border-0 dark:bg-claude-darkBg bg-claude-bg"
        referrerPolicy="strict-origin-when-cross-origin"
        allow="camera; microphone; clipboard-read; clipboard-write; fullscreen"
      />
    </div>
  );
};

export default ExternalAppView;
